import { ReadingUnitType, SpeakingJobType } from 'src/app/core/reading-utils/enums';
import { ReadingAtom } from '../reading-utils/interfaces';
import { BehaviorSubject, Observable } from 'rxjs';
import { HighlightingTypes } from '../pdf-utils/enums';
import { ICustomTextAreaState, ILanguage } from '../interfaces';
import { IAutoSaveDocChanges } from 'src/app/services/autosave/autosave.interface';

interface IHighlightingItem {
  words: ReadingAtom[];
  itemType: HighlightingTypes;
  color: string;
  active: boolean;
}

export class AsgardDocBase {
  protected readonly currentHighlight: IHighlightingItem[] = [];

  protected langMapper: (...args) => ILanguage;
  protected clickItemSelected: boolean = false;
  protected readingItemSubject: BehaviorSubject<ReadingAtom[] | null> = new BehaviorSubject(null);
  private documentHash: string | null = null;

  public set docHash(hash: string | null) {
    this.documentHash = hash;
  }

  public get docHash(): string | null {
    return (this.documentHash || this.documentId);
  }

  constructor(public readonly documentId: string) { }

  get isDocumentReady(): boolean { return false; }
  get isReadingItemClicked(): boolean { return this.clickItemSelected; }
  get readingItemObservable(): Observable<ReadingAtom[] | null> { return this.readingItemSubject.asObservable(); }
  get readingItem(): ReadingAtom[] | null { return this.readingItemSubject.getValue(); }
  get editorElement(): HTMLElement | null { return null; }
  get editorQueryStr(): string | null { return null; }
  get editorFrame(): HTMLIFrameElement | null { return null; }
  get editorWin(): Window | null { return null; }
  get hasReadingItem(): boolean { return this.readingItem !== null && this.readingItem.length > 0 && this.readingItem[0] !== null; }

  protected setReadingItem(item: ReadingAtom[], clicked: boolean = false): void {
    this.clickItemSelected = clicked;
    this.readingItemSubject.next(item);
  }

  protected beforeHighlight(words: ReadingAtom[], color: string, itemType: HighlightingTypes, exclusive: boolean = true): void {
    this.clearTextHighlight(exclusive);

    const newHighlight = { color, words, itemType, active: false };

    if (!exclusive) {
      const currHighlightIdx = this.currentHighlight.findIndex(x => x.itemType === itemType);

      if (currHighlightIdx >= 0) {
        this.currentHighlight[currHighlightIdx] = newHighlight;
      } else {
        this.currentHighlight.push(newHighlight);
      }
    } else {
      this.currentHighlight.splice(0, this.currentHighlight.length);
      this.currentHighlight.push(newHighlight);
    }
  }

  async selectReadingUnitByClick(_e: MouseEvent, _unit: ReadingUnitType, _isOrderedReading: boolean = false): Promise<void> {
    throw new Error('Not implemented.');
  }

  async selectHoverWord(_e: MouseEvent) {
    throw new Error('Not implemented.');
  }

  async goToFirstReadingItem(_itemType: ReadingUnitType) {
    throw new Error('Not implemented.');
  }

  async goToNextReadingItem(_itemType: ReadingUnitType) {
    throw new Error('Not implemented.');
  }

  async goToFirstItem(_itemType: ReadingUnitType) {
    throw new Error('Not implemented.');
  }

  async goToLastItem(_itemType: ReadingUnitType) {
    throw new Error('Not implemented.');
  }

  async startNextItemFrom(_itemType: ReadingUnitType) {
    throw new Error('Not implemented.');
  }

  async goToPrevReadingItem(_itemType: ReadingUnitType) {
    throw new Error('Not implemented.');
  }

  async doHighlight(_words: ReadingAtom[], _color: string, _itemType: HighlightingTypes, _exclusive: boolean = true) {
    throw new Error('Not implemented.');
  }

  async getSpeakingJobType(): Promise<SpeakingJobType> {
    return SpeakingJobType.Document;
  }

  setDocumentOptins(lngMpr: (...args) => ILanguage, ..._args): void {
    this.langMapper = lngMpr;
  }

  isUnitHighlighted(itemType: HighlightingTypes | ReadingUnitType): boolean {
    const mappedType = itemType === ReadingUnitType.Sentence ? HighlightingTypes.Sentence : HighlightingTypes.Word;
    return typeof this.currentHighlight.find(x => x.itemType === mappedType && x.active) === 'undefined' ? false : true;
  }

  clearTextHighlight(force: boolean = false, _clicked: boolean = false): void {
    if (force) {
      this.currentHighlight.splice(0, this.currentHighlight.length);
    }
  }

  hasSelection(): boolean {
    throw new Error('Not implemented.');
  }

  async setSelectionToReadingItem() {
    throw new Error('Not implemented.');
  }

  setCurrentPageIdx(_value: number) {
    throw new Error('Not implemented.');
  }

  getItemUnderCursor(_itemType: ReadingUnitType): ReadingAtom[] | null {
    throw new Error('Not implemented.');
  }

  getItemBeforeCursor(_itemType: ReadingUnitType): ReadingAtom[] | null {
    throw new Error('Not implemented.');
  }

  getCurrentPageIdx(): number {
    throw new Error('Not implemented.');
  }

  getShowedDocRect(): { x1: number, y1: number, x2: number, y2: number } | null {
    throw new Error('Not implemented.');
  }

  hasNextItemToRead(_itemType: ReadingUnitType): boolean {
    throw new Error('Not implemented.');
  }

  hasPrevItemToRead(): boolean {
    throw new Error('Not implemented.');
  }

  scrollToWord(_word: ReadingAtom) {
    throw new Error('Not implemented.');
  }

  selectedText(): string {
    throw new Error('Not implemented.');
  }

  openCustomTextNote(_e: MouseEvent): ICustomTextAreaState {
    throw new Error('Not implemented.');
  }

  drawTextAnnotation(_e: ICustomTextAreaState) {
    throw new Error('Not implemented.');
  }

  calcualteTextNoteRestictions(_pageIdx: number): Partial<DOMRect> {
    throw new Error('Not implemented.');
  }

  async getDocumentUnsavedChanged(): Promise<IAutoSaveDocChanges> {
    throw new Error('Not implemented.');
  }

  async applyDocumentChanges(_docHash: string, _changes: string): Promise<void> {
    throw new Error('Not implemented.');
  }

  async lastReadingPositionFounded(_pageIdx: number | null, _readingItemIdx: number | null, _readingItemPageIdx: number | null, _itemType: ReadingUnitType) {
    throw new Error('Not implemented.');
  }

  getReadingPostition(): number | null {
    let readingItemIdx: number | null = null;
    let val = this.readingItemSubject.getValue();
    if (val && val.length !== 0 && val[0]) {
      readingItemIdx = val[0].itemIdx;
    }
    return readingItemIdx;
  }


  public triggerArrowUpDown(ev: KeyboardEvent, elem: Element) {
    if (elem) {
      const scrollValue = 140;
      let valueToScroll = 0;
      if (elem) {
        switch (ev.key) {
          case 'ArrowUp':
            valueToScroll = elem.scrollTop - scrollValue;
            break;
          case 'ArrowDown':
            valueToScroll = elem.scrollTop + scrollValue;
            break;
          default:
            return;
        }
        elem.scrollTo({ top: valueToScroll, left: 0, behavior: 'auto' });
      }
    }
  }

}
